import React, {
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import {
  AnimatePresence,
  motion
} from 'framer-motion';

import * as FiIcons from 'react-icons/fi';

import SafeIcon from '../../common/SafeIcon';
import SearchableSelect from '../common/SearchableSelect';

import transactionImportService from '../../services/transactionImportService';

import supabase from '../../supabase/supabase';

const {
  FiAlertCircle,
  FiCheckCircle,
  FiChevronLeft,
  FiChevronRight,
  FiFileText,
  FiLoader,
  FiX,
  FiCpu,
  FiEdit3
} = FiIcons;

/* =========================================================
   CSV FIELDS
========================================================= */

const fields = [
  ['transaction_date', 'Transaction Date'],
  ['description', 'Description'],
  ['amount', 'Amount'],
  ['debit', 'Debit'],
  ['credit', 'Credit'],
  ['balance', 'Balance'],
  ['currency', 'Currency']
];

/* =========================================================
   AMOUNT MODES
========================================================= */

const amountModes = [
  [
    'signed',
    'Positive = Credit / Negative = Debit'
  ],
  [
    'positive_debit',
    'Positive = Debit / Negative = Credit'
  ],
  [
    'all_debit',
    'All amounts = Debit'
  ],
  [
    'all_credit',
    'All amounts = Credit'
  ]
];

/* =========================================================
   DATE FORMATS
========================================================= */

const dateFormats = [
  ['YYYY-MM-DD', 'YYYY-MM-DD'],
  ['MM/DD/YYYY', 'MM/DD/YYYY'],
  ['DD/MM/YYYY', 'DD/MM/YYYY'],
  ['MM/DD/YY', 'MM/DD/YY'],
  ['DD/MM/YY', 'DD/MM/YY']
];

/* =========================================================
   INITIAL MAPPING
========================================================= */

const createInitialMapping = () => ({
  transaction_date: null,
  description: null,
  amount: null,
  debit: null,
  credit: null,
  balance: null,
  currency: null,
  headers: []
});

/* =========================================================
   STEP BAR
========================================================= */

function StepBar({ step }) {
  const steps = [
    'Select CSV',
    'Map columns',
    'Preview',
    'Review & Categorize',
    'Finish'
  ];

  return (
    <div className="flex items-center gap-2">
      {steps.map((label, index) => (
        <React.Fragment key={label}>
          <div
            className={`flex items-center gap-2 text-xs font-semibold ${
              index <= step
                ? 'text-slate-900'
                : 'text-slate-400'
            }`}
          >
            <span
              className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] ${
                index < step
                  ? 'bg-emerald-600 text-white'
                  : index === step
                    ? 'bg-slate-950 text-white'
                    : 'bg-slate-100 text-slate-400'
              }`}
            >
              {index < step ? (
                <SafeIcon icon={FiCheckCircle} />
              ) : (
                index + 1
              )}
            </span>

            <span className="hidden sm:inline">
              {label}
            </span>
          </div>

          {index < steps.length - 1 && (
            <span
              className={`h-px flex-1 ${
                index < step
                  ? 'bg-emerald-300'
                  : 'bg-slate-200'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* =========================================================
   IMPORT ACCOUNT SELECTOR
========================================================= */

function ImportAccountSelector({
  accounts,
  selectedAccountId,
  onChange,
  loading,
  error
}) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <label className="text-sm font-semibold text-slate-900">
        Import Account{' '}
        <span className="text-rose-500">*</span>
      </label>

      <p className="mt-1 text-xs text-slate-500">
        Select the Chart of Account where
        these imported transactions belong.
      </p>

      <div className="mt-3">
        <SearchableSelect
          options={accounts}
          value={selectedAccountId || ''}
          onChange={(value) =>
            onChange(value || null)
          }
          disabled={loading}
          placeholder={
            loading
              ? 'Loading accounts...'
              : 'Select an account...'
          }
          searchPlaceholder="Search accounts by code or name..."
          emptyMessage={
            error ||
            'No active accounts are available for this organization.'
          }
          getLabel={(account) =>
            `${account.code || 'No code'} - ${account.name}`
          }
          getSearchText={(account) => [
            account.code,
            account.name,
            account.type,
            account.sub_type
          ]}
          menuWidth={420}
        />
      </div>

      <p className="mt-2 text-xs text-slate-500">
        This is the source/import account.
        ProfitYO AI determines the transaction
        category separately.
      </p>
    </div>
  );
}

/* =========================================================
   SUMMARY CARD
========================================================= */

function SummaryCard({
  label,
  value,
  tone = 'slate'
}) {
  const colors = {
    slate: 'bg-slate-50 text-slate-900',
    green: 'bg-emerald-50 text-emerald-700',
    rose: 'bg-rose-50 text-rose-700',
    amber: 'bg-amber-50 text-amber-700',
    blue: 'bg-blue-50 text-blue-700'
  };

  return (
    <div
      className={`rounded-2xl p-4 ${colors[tone]}`}
    >
      <p className="text-xs font-medium opacity-70">
        {label}
      </p>

      <p className="mt-1 text-2xl font-semibold">
        {value}
      </p>
    </div>
  );
}

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function CsvImportModal({
  isOpen,
  onClose,
  organizationId,
  userId,
  organizationName,
  organizationCurrency = 'USD'
}) {
  const inputRef = useRef(null);

  const mappingRef = useRef(
    createInitialMapping()
  );

  /* =======================================================
     STATE
  ======================================================= */

  const [step, setStep] = useState(0);

  const [file, setFile] = useState(null);

  const [parsed, setParsed] = useState(null);

  const [mapping, setMapping] = useState(
    createInitialMapping()
  );

  const [amountMode, setAmountMode] =
    useState('signed');

  const [dateFormat, setDateFormat] =
    useState(null);

  const [
    dateFormatIsAmbiguous,
    setDateFormatIsAmbiguous
  ] = useState(false);

  const [normalized, setNormalized] =
    useState(null);

  const [accounts, setAccounts] = useState([]);

  const [
    selectedAccountId,
    setSelectedAccountId
  ] = useState(null);

  const [
    accountsLoading,
    setAccountsLoading
  ] = useState(false);

  const [
    accountsError,
    setAccountsError
  ] = useState('');

  const [busy, setBusy] = useState(false);

  const [error, setError] = useState('');

  const [result, setResult] = useState(null);

  const [
    categorizationStatus,
    setCategorizationStatus
  ] = useState('');

  const [
    reviewRows,
    setReviewRows
  ] = useState([]);

  const [
    reviewLoading,
    setReviewLoading
  ] = useState(false);

  const [
    updatingCategoryId,
    setUpdatingCategoryId
  ] = useState(null);

  /* =======================================================
     SELECTED ACCOUNT
  ======================================================= */

  const selectedAccount = useMemo(
    () =>
      accounts.find(
        (account) =>
          account.id === selectedAccountId
      ),
    [
      accounts,
      selectedAccountId
    ]
  );

  /* =======================================================
     PREVIEW ROWS
  ======================================================= */

  const previewRows = useMemo(
    () =>
      normalized?.valid?.slice(
        0,
        12
      ) || [],
    [normalized]
  );

  /* =======================================================
     REVIEW SUMMARY
  ======================================================= */

  const reviewSummary = useMemo(() => {
    const rows = reviewRows || [];

    const nonDuplicates =
      rows.filter(
        (row) => !row.is_duplicate
      );

    const categorized =
      nonDuplicates.filter(
        (row) =>
          Boolean(
            row.suggested_account_id
          )
      );

    const missing =
      nonDuplicates.filter(
        (row) =>
          !row.suggested_account_id
      );

    const duplicates =
      rows.filter(
        (row) => row.is_duplicate
      );

    return {
      total: rows.length,
      categorized: categorized.length,
      missing: missing.length,
      duplicates: duplicates.length,
      ready:
        nonDuplicates.length > 0 &&
        missing.length === 0
    };
  }, [reviewRows]);

  /* =======================================================
     AVAILABLE CSV COLUMNS
  ======================================================= */

  const getAvailableColumns = (
    field
  ) => {
    const csvHeaders =
      parsed?.headers || [];

    const selectedIndexes =
      Object.entries(mapping)
        .filter(
          ([key]) =>
            key !== 'headers'
        )
        .map(
          ([, value]) => value
        )
        .filter(
          (value) =>
            Number.isInteger(value)
        );

    return csvHeaders
      .map(
        (header, index) => ({
          header,
          index
        })
      )
      .filter(
        ({ index }) =>
          mapping[field] === index ||
          !selectedIndexes.includes(
            index
          )
      );
  };

  /* =======================================================
     KEEP MAPPING REF UPDATED
  ======================================================= */

  useEffect(() => {
    mappingRef.current = mapping;
  }, [mapping]);

  /* =======================================================
     LOAD ACCOUNTS
  ======================================================= */

  useEffect(() => {
    let mounted = true;

    if (!isOpen) {
      return undefined;
    }

    const loadAccounts = async () => {
      if (!organizationId) {
        setAccounts([]);
        setAccountsError(
          'No active organization is selected.'
        );
        return;
      }

      setAccountsLoading(true);
      setAccountsError('');

      try {
        const data =
          await transactionImportService.getImportAccounts(
            organizationId
          );

        if (mounted) {
          setAccounts(data || []);
        }
      } catch (accountError) {
        console.error(
          'Import accounts could not be loaded:',
          accountError
        );

        if (mounted) {
          setAccounts([]);

          setAccountsError(
            'Chart of Accounts could not be loaded. Refresh and try again.'
          );
        }
      } finally {
        if (mounted) {
          setAccountsLoading(false);
        }
      }
    };

    loadAccounts();

    return () => {
      mounted = false;
    };
  }, [
    isOpen,
    organizationId
  ]);

  /* =======================================================
     RESET
  ======================================================= */

  const reset = () => {
    const freshMapping =
      createInitialMapping();

    setStep(0);
    setFile(null);
    setParsed(null);

    mappingRef.current =
      freshMapping;

    setMapping(freshMapping);

    setAmountMode('signed');

    setDateFormat(null);

    setDateFormatIsAmbiguous(false);

    setNormalized(null);

    setSelectedAccountId(null);

    setBusy(false);

    setError('');

    setResult(null);

    setCategorizationStatus('');

    setReviewRows([]);

    setReviewLoading(false);

    setUpdatingCategoryId(null);

    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  /* =======================================================
     CLOSE
  ======================================================= */

  const close = () => {
    if (busy) {
      return;
    }

    reset();
    onClose();
  };

  /* =======================================================
     SELECT CSV
  ======================================================= */

  const selectFile = async (event) => {
    const selected =
      event.target.files?.[0];

    if (!selected) {
      return;
    }

    setBusy(true);
    setError('');

    try {
      const csv =
        await transactionImportService.parseFile(
          selected
        );

      const headers =
        Array.isArray(csv.headers)
          ? csv.headers
          : [];

      const rows = (
        csv.rows || []
      ).map((row) => {
        if (Array.isArray(row)) {
          return row.map(
            (value) =>
              value === null ||
              value === undefined
                ? ''
                : String(value)
          );
        }

        return headers.map(
          (header) => {
            const value =
              row?.[header];

            return value === null ||
              value === undefined
              ? ''
              : String(value);
          }
        );
      });

      const parsedCsv = {
        ...csv,
        headers,
        rows
      };

      console.log(
        'CSV headers:',
        headers
      );

      console.log(
        'CSV first row:',
        rows[0]
      );

      const detectedColumns =
        transactionImportService.detectColumns(
          headers
        );

      const detectedMapping = {
        ...createInitialMapping(),
        headers,

        ...Object.fromEntries(
          fields.map(
            ([field]) => [
              field,
              Number.isInteger(
                detectedColumns[field]
              )
                ? detectedColumns[field]
                : null
            ]
          )
        )
      };

      console.log(
        'Detected columns:',
        detectedColumns
      );

      console.log(
        'Detected mapping:',
        detectedMapping
      );

      mappingRef.current =
        detectedMapping;

      setFile(selected);

      setParsed(parsedCsv);

      setMapping(detectedMapping);

      const detectedDate =
        transactionImportService.detectDateFormat(
          rows,
          detectedMapping
        );

      setDateFormat(
        detectedDate?.format ||
          detectedDate ||
          null
      );

      setDateFormatIsAmbiguous(
        Boolean(
          detectedDate?.ambiguous
        )
      );

      setNormalized(null);

      setResult(null);

      setReviewRows([]);

      setCategorizationStatus('');

      setStep(1);
    } catch (parseError) {
      console.error(
        'CSV parsing failed:',
        parseError
      );

      setError(
        parseError?.message ||
          'The CSV could not be read.'
      );
    } finally {
      setBusy(false);
    }
  };

  /* =======================================================
     UPDATE MAPPING
  ======================================================= */

  const updateMapping = (
    field,
    value
  ) => {
    const nextValue =
      value === '' ||
      value === null
        ? null
        : Number(value);

    setMapping((current) => {
      const next = {
        ...current,

        [field]:
          Number.isInteger(
            nextValue
          )
            ? nextValue
            : null
      };

      fields.forEach(
        ([otherField]) => {
          if (
            otherField !== field &&
            next[field] !== null &&
            next[otherField] ===
              next[field]
          ) {
            next[otherField] = null;
          }
        }
      );

      mappingRef.current = next;

      if (
        field ===
          'transaction_date' &&
        parsed?.rows
      ) {
        const detectedDate =
          transactionImportService.detectDateFormat(
            parsed.rows,
            next
          );

        setDateFormat(
          detectedDate?.format ||
            detectedDate ||
            null
        );

        setDateFormatIsAmbiguous(
          Boolean(
            detectedDate?.ambiguous
          )
        );
      }

      return next;
    });

    setError('');
  };

  /* =======================================================
     VALIDATE + PREVIEW
  ======================================================= */

  const validateMappingAndPreview =
    () => {
      try {
        const currentMapping =
          mappingRef.current;

        const isMapped =
          (value) =>
            Number.isInteger(value) &&
            value >= 0;

        if (
          !isMapped(
            currentMapping.transaction_date
          )
        ) {
          throw new Error(
            'Please map the Transaction Date column.'
          );
        }

        if (
          !isMapped(
            currentMapping.description
          )
        ) {
          throw new Error(
            'Please map the Description column.'
          );
        }

        const hasDebitCredit =
          isMapped(
            currentMapping.debit
          ) &&
          isMapped(
            currentMapping.credit
          );

        if (
          !hasDebitCredit &&
          !isMapped(
            currentMapping.amount
          )
        ) {
          throw new Error(
            'Please map the Amount column.'
          );
        }

        if (!selectedAccountId) {
          throw new Error(
            'Please select an account before importing transactions.'
          );
        }

        if (
          dateFormatIsAmbiguous &&
          !dateFormat
        ) {
          throw new Error(
            'Choose a date format before continuing.'
          );
        }

        const normalizedRows =
          transactionImportService.normalizeRows(
            {
              rows: parsed.rows,
              mapping: currentMapping,
              amountMode,
              dateFormat,
              currency:
                organizationCurrency
            }
          );

        console.log(
          'Normalized rows:',
          normalizedRows
        );

        const validated =
          transactionImportService.validateRows(
            normalizedRows
          );

        console.log(
          'Validated rows:',
          validated
        );

        setNormalized(validated);

        setStep(2);

        setError('');
      } catch (mappingError) {
        console.error(
          'CSV mapping/validation failed:',
          mappingError
        );

        setError(
          mappingError?.message ||
            'The CSV could not be validated.'
        );
      }
    };

  /* =======================================================
     LOAD REVIEW DATA
  ======================================================= */

  const loadReviewData = async (
    importBatchId
  ) => {
    if (
      !organizationId ||
      !importBatchId
    ) {
      throw new Error(
        'Organization or import batch is missing.'
      );
    }

    setReviewLoading(true);

    try {
      const data =
        await transactionImportService.getImportReviewData(
          organizationId,
          importBatchId
        );

      const rows = Array.isArray(
        data?.rows
      )
        ? data.rows
        : [];

      console.log(
        '[CSV IMPORT] Review data loaded:',
        {
          batch: data?.batch,
          rowCount: rows.length
        }
      );

      setReviewRows(rows);

      return data;
    } finally {
      setReviewLoading(false);
    }
  };

  /* =======================================================
     STAGE + AI CATEGORIZATION
  ======================================================= */

  const startAiCategorization =
    async () => {
      if (
        !organizationId ||
        !userId ||
        !selectedAccountId
      ) {
        setError(
          'Missing organization, user, or source account.'
        );

        return;
      }

      if (
        !normalized?.valid?.length
      ) {
        setError(
          'There are no valid transactions to import.'
        );

        return;
      }

      try {
        setBusy(true);

        setError('');

        setStep(3);

        setCategorizationStatus(
          'Staging transactions...'
        );

        /* ---------------------------------------------
           1. STAGE
        --------------------------------------------- */

        const staged =
          await transactionImportService.stageImport(
            {
              organizationId,

              fileName:
                file?.name ||
                'import.csv',

              rows: normalized.valid,

              sourceAccountId:
                selectedAccountId,

              createdBy: userId
            }
          );

        const importBatchId =
          staged?.batch?.id;

        if (!importBatchId) {
          throw new Error(
            'Import batch was not created.'
          );
        }

        console.log(
          'IMPORT STAGED:',
          {
            importBatchId,
            organizationId,
            selectedAccountId,
            validCount:
              normalized.valid.length,
            invalidCount:
              normalized.invalid.length
          }
        );

        /* ---------------------------------------------
           2. AI CATEGORIZATION
        --------------------------------------------- */

        setCategorizationStatus(
          'ProfitYO AI is categorizing transactions...'
        );

        const {
          data: categorizationResult,
          error: categorizationError
        } =
          await supabase.functions.invoke(
            'ai-transaction-categorizer',
            {
              body: {
                organizationId,
                importBatchId
              }
            }
          );

        console.log(
          'AI CATEGORIZATION RESULT:',
          categorizationResult
        );

        if (categorizationError) {
          console.error(
            'AI CATEGORIZATION ERROR:',
            categorizationError
          );

          throw new Error(
            categorizationError.message ||
              'AI categorization failed.'
          );
        }

        if (!categorizationResult) {
          throw new Error(
            'AI categorization returned no response.'
          );
        }

        /* ---------------------------------------------
           3. READ AI RESULT
        --------------------------------------------- */

        const categorizedCount =
          Number(
            categorizationResult.categorized ??
              categorizationResult.categorizedCount ??
              0
          );

        const fallbackCount =
          Number(
            categorizationResult.fallback ??
              categorizationResult.fallbackCount ??
              0
          );

        const failedCount =
          Number(
            categorizationResult.failed ??
              categorizationResult.failedCount ??
              0
          );

        const totalCount =
          Number(
            categorizationResult.total ??
              normalized.valid.length
          );

        /* ---------------------------------------------
           4. READ ACTUAL DATABASE DATA
        --------------------------------------------- */

        setCategorizationStatus(
          'Loading transactions for review...'
        );

        const reviewData =
          await loadReviewData(
            importBatchId
          );

        /*
         * getImportReviewData() returns:
         *
         * {
         *   batch,
         *   rows
         * }
         *
         * It does NOT return an array directly.
         */
        if (
          !reviewData ||
          !Array.isArray(
            reviewData.rows
          )
        ) {
          throw new Error(
            'The import transactions could not be loaded after categorization.'
          );
        }

        console.log(
          'IMPORT REVIEW DATA:',
          {
            batch:
              reviewData.batch,
            rowCount:
              reviewData.rows.length,
            rows:
              reviewData.rows
          }
        );

        /* ---------------------------------------------
           5. SAVE RESULT
        --------------------------------------------- */

        setResult({
          ...staged,

          importBatchId,

          categorization:
            categorizationResult,

          validCount:
            normalized.valid.length,

          invalidCount:
            normalized.invalid.length,

          categorizedCount,

          fallbackCount,

          failedCount,

          totalCount,

          reviewData
        });

        setCategorizationStatus('');

      } catch (err) {
        console.error(
          'CSV AI categorization error:',
          err
        );

        setCategorizationStatus('');

        setError(
          err instanceof Error
            ? err.message
            : 'Failed to categorize CSV transactions.'
        );
      } finally {
        setBusy(false);
      }
    };

  /* =======================================================
     UPDATE AI CATEGORY
  ======================================================= */

  const updateCategory = async (
    row,
    accountId
  ) => {
    if (
      !row?.id ||
      !accountId
    ) {
      return;
    }

    /*
     * The batch ID belongs to the staged
     * import result. It is not a local
     * variable in this function.
     */
    const importBatchId =
      result?.importBatchId ||
      result?.batch?.id;

    if (
      !organizationId ||
      !importBatchId
    ) {
      setError(
        'The import batch could not be found.'
      );

      return;
    }

    try {
      setUpdatingCategoryId(
        row.id
      );

      setError('');

      const updated =
        await transactionImportService.updateSuggestedAccount({
          organizationId,
          importBatchId,
          importTransactionId: row.id,
          suggestedAccountId: accountId
        });

      /*
       * updateSuggestedAccount() only changes
       * suggested_account_id/status/updated_at.
       *
       * Existing:
       * - source_account_id
       * - confidence
       * - ai_reason
       *
       * remain untouched.
       */
      setReviewRows(
        (current) =>
          current.map(
            (item) =>
              item.id === row.id
                ? {
                    ...item,
                    ...updated
                  }
                : item
          )
      );

    } catch (updateError) {
      console.error(
        'Failed to update category:',
        updateError
      );

      setError(
        updateError?.message ||
          'Failed to update transaction category.'
      );
    } finally {
      setUpdatingCategoryId(null);
    }
  };

  /* =======================================================
     APPROVE + IMPORT
  ======================================================= */

  const approveAndImport =
    async () => {
      const importBatchId =
        result?.importBatchId ||
        result?.batch?.id;

      if (
        !organizationId ||
        !importBatchId
      ) {
        setError(
          'The import batch could not be found.'
        );

        return;
      }

      try {
        setBusy(true);

        setError('');

        setCategorizationStatus(
          'Checking transaction categories...'
        );

        /* ---------------------------------------------
           VALIDATE
        --------------------------------------------- */

        const validation =
          await transactionImportService.validateReadyForApproval(
            {
              organizationId,
              importBatchId
            }
          );

        if (!validation.valid) {
          throw new Error(
            'Please assign a category to all transactions before importing.'
          );
        }

        /* ---------------------------------------------
           APPROVE
        --------------------------------------------- */

        setCategorizationStatus(
          'Posting transactions to the General Ledger...'
        );

        console.log(
          'APPROVING IMPORT:',
          {
            organizationId,
            importBatchId
          }
        );

        const {
          data: approvalResult,
          error: approvalError
        } =
          await supabase.functions.invoke(
            'approve-import',
            {
              body: {
                organizationId,
                importBatchId
              }
            }
          );

        console.log(
          'APPROVE IMPORT RESULT:',
          approvalResult
        );

        if (approvalError) {
          console.error(
            'APPROVE IMPORT ERROR:',
            approvalError
          );

          throw new Error(
            approvalError.message ||
              'Failed to approve and import transactions.'
          );
        }

        if (!approvalResult) {
          throw new Error(
            'The approval function returned no response.'
          );
        }

        if (approvalResult.error) {
          throw new Error(
            approvalResult.error
          );
        }

        const importedCount =
          Number(
            approvalResult.importedCount ??
              approvalResult.imported ??
              approvalResult.count ??
              0
          );

        console.log(
          'TRANSACTIONS IMPORTED:',
          importedCount
        );

        setResult(
          (current) => ({
            ...current,

            approval:
              approvalResult,

            importedCount
          })
        );

        setCategorizationStatus('');

        setStep(4);

      } catch (approvalError) {
        console.error(
          'CSV approval/import failed:',
          approvalError
        );

        setCategorizationStatus('');

        setError(
          approvalError?.message ||
            'Failed to approve and import transactions.'
        );
      } finally {
        setBusy(false);
      }
    };

  /* =======================================================
     RENDER
  ======================================================= */

  if (!isOpen) {
    return null;
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">

        {/* BACKDROP */}

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
          className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        />

        {/* MODAL */}

        <motion.section
          initial={{
            opacity: 0,
            y: 18,
            scale: 0.98
          }}
          animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }}
          exit={{
            opacity: 0,
            y: 18,
            scale: 0.98
          }}
          className="relative z-10 flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl"
        >

          {/* HEADER */}

          <header className="flex items-center justify-between border-b border-slate-100 px-5 py-4 md:px-7">

            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">
                Import Data
              </p>

              <h2 className="mt-1 text-xl font-semibold tracking-tight text-slate-950">
                Import transactions from CSV
              </h2>

              <p className="mt-1 text-xs text-slate-500">
                {organizationName ||
                  'Active organization'}{' '}
                · staged, categorized,
                reviewed, and imported
              </p>
            </div>

            <button
              type="button"
              onClick={close}
              disabled={busy}
              className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50"
              aria-label="Close CSV import"
            >
              <SafeIcon icon={FiX} />
            </button>

          </header>

          {/* STEP BAR */}

          <div className="border-b border-slate-100 px-5 py-4 md:px-7">
            <StepBar step={step} />
          </div>

          {/* CONTENT */}

          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 md:px-7">

            {/* ERROR */}

            {error && (
              <div className="mb-5 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">

                <SafeIcon
                  icon={FiAlertCircle}
                  className="mt-0.5 shrink-0"
                />

                <p>{error}</p>

              </div>
            )}

            {/* PROCESSING */}

            {busy &&
              categorizationStatus && (
                <div className="mb-5 flex items-center gap-3 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">

                  <SafeIcon
                    icon={FiLoader}
                    className="animate-spin"
                  />

                  <p>
                    {categorizationStatus}
                  </p>

                </div>
              )}

            {/* =================================================
               STEP 0
            ================================================= */}

            {step === 0 && (
              <div className="flex min-h-[300px] flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 p-8 text-center">

                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm">
                  <SafeIcon
                    icon={FiFileText}
                    className="text-2xl"
                  />
                </div>

                <h3 className="text-base font-semibold text-slate-900">
                  Choose a CSV statement
                </h3>

                <p className="mt-2 max-w-md text-sm text-slate-500">
                  CSV files with quoted
                  descriptions, commas,
                  blank cells, debit and
                  credit columns are
                  supported.
                </p>

                <input
                  ref={inputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={selectFile}
                  className="hidden"
                />

                <button
                  type="button"
                  onClick={() =>
                    inputRef.current?.click()
                  }
                  disabled={busy}
                  className="mt-6 inline-flex items-center gap-2 rounded-xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
                >
                  {busy && (
                    <SafeIcon
                      icon={FiLoader}
                      className="animate-spin"
                    />
                  )}

                  Select CSV
                </button>

              </div>
            )}

            {/* =================================================
               STEP 1
            ================================================= */}

            {step === 1 && parsed && (
              <div className="space-y-5">

                <div className="flex flex-wrap items-center justify-between gap-3">

                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {file?.name}
                    </p>

                    <p className="text-xs text-slate-500">
                      {parsed.rows.length}{' '}
                      non-empty transaction
                      rows detected
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      inputRef.current?.click()
                    }
                    disabled={busy}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    Choose another file
                  </button>

                </div>

                <ImportAccountSelector
                  accounts={accounts}
                  selectedAccountId={
                    selectedAccountId
                  }
                  onChange={(accountId) => {
                    setSelectedAccountId(
                      accountId
                    );

                    setError('');
                  }}
                  loading={accountsLoading}
                  error={accountsError}
                />

                {/* COLUMN MAPPING */}

                <div className="overflow-x-auto rounded-2xl border border-slate-200">

                  <table className="min-w-full text-left text-sm">

                    <thead className="bg-slate-50">

                      <tr>

                        <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">
                          ProfitYO field
                        </th>

                        <th className="px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">
                          CSV column
                        </th>

                      </tr>

                    </thead>

                    <tbody className="divide-y divide-slate-100">

                      {fields.map(
                        ([field, label]) => (
                          <tr key={field}>

                            <td className="px-4 py-3 font-medium text-slate-800">
                              {label}
                            </td>

                            <td className="px-4 py-3">

                              <select
                                value={
                                  mapping[field] ??
                                  ''
                                }
                                onChange={(
                                  event
                                ) =>
                                  updateMapping(
                                    field,
                                    event.target
                                      .value
                                  )
                                }
                                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500"
                              >

                                <option value="">
                                  Ignore
                                </option>

                                {getAvailableColumns(
                                  field
                                ).map(
                                  ({
                                    header,
                                    index
                                  }) => (
                                    <option
                                      key={`${header}-${index}`}
                                      value={index}
                                    >
                                      {header}
                                    </option>
                                  )
                                )}

                              </select>

                            </td>

                          </tr>
                        )
                      )}

                    </tbody>

                  </table>

                </div>

                {/* AMOUNT MODE */}

                {mapping.amount !== null && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">

                    <p className="text-sm font-semibold text-slate-900">
                      How should Amount be
                      interpreted?
                    </p>

                    <div className="mt-3 grid gap-2 sm:grid-cols-2">

                      {amountModes.map(
                        ([value, label]) => (
                          <label
                            key={value}
                            className="flex items-center gap-2 text-sm text-slate-600"
                          >

                            <input
                              type="radio"
                              name="amount-mode"
                              value={value}
                              checked={
                                amountMode ===
                                value
                              }
                              onChange={(
                                event
                              ) =>
                                setAmountMode(
                                  event.target
                                    .value
                                )
                              }
                            />

                            {label}

                          </label>
                        )
                      )}

                    </div>

                  </div>
                )}

                {/* DATE FORMAT */}

                {mapping.transaction_date !==
                  null && (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">

                    <p className="text-sm font-semibold text-slate-900">
                      Date format
                    </p>

                    <p className="mt-1 text-xs text-slate-500">
                      {dateFormatIsAmbiguous
                        ? 'This CSV date format is ambiguous. Choose the format used by this file.'
                        : `Detected format: ${
                            dateFormat ||
                            'Choose a format if dates need review.'
                          }`}
                    </p>

                    <div className="mt-3 grid gap-2 sm:grid-cols-2">

                      {dateFormats.map(
                        ([value, label]) => (
                          <label
                            key={value}
                            className="flex items-center gap-2 text-sm text-slate-600"
                          >

                            <input
                              type="radio"
                              name="date-format"
                              value={value}
                              checked={
                                dateFormat ===
                                value
                              }
                              onChange={(
                                event
                              ) =>
                                setDateFormat(
                                  event.target
                                    .value
                                )
                              }
                            />

                            {label}

                          </label>
                        )
                      )}

                    </div>

                  </div>
                )}

                {/* CSV PREVIEW */}

                <div className="overflow-x-auto rounded-2xl border border-slate-200">

                  <table className="min-w-full text-left text-xs">

                    <thead className="bg-slate-50">

                      <tr>

                        {parsed.headers.map(
                          (
                            header,
                            index
                          ) => (
                            <th
                              key={`${header}-${index}`}
                              className="whitespace-nowrap px-3 py-2 font-bold text-slate-500"
                            >
                              {header}
                            </th>
                          )
                        )}

                      </tr>

                    </thead>

                    <tbody className="divide-y divide-slate-100">

                      {parsed.rows
                        .slice(0, 5)
                        .map(
                          (
                            row,
                            rowIndex
                          ) => (
                            <tr
                              key={rowIndex}
                            >

                              {parsed.headers.map(
                                (
                                  _header,
                                  cellIndex
                                ) => (
                                  <td
                                    key={
                                      cellIndex
                                    }
                                    className="max-w-[220px] truncate px-3 py-2 text-slate-600"
                                  >
                                    {row[
                                      cellIndex
                                    ] ||
                                      '—'}
                                  </td>
                                )
                              )}

                            </tr>
                          )
                        )}

                    </tbody>

                  </table>

                </div>

              </div>
            )}

            {/* =================================================
               STEP 2 - PREVIEW
            ================================================= */}

            {step === 2 &&
              normalized && (
                <div className="space-y-5">

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">

                    <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">
                      Import Account
                    </p>

                    <p className="mt-1 text-sm font-semibold text-slate-900">
                      {selectedAccount
                        ? `${
                            selectedAccount.code ||
                            'No code'
                          } - ${
                            selectedAccount.name
                          }`
                        : 'No account selected'}
                    </p>

                    <p className="mt-1 text-xs text-slate-500">
                      This account will be
                      stored as the source
                      account. AI categorization
                      is stored separately.
                    </p>

                  </div>

                  <div className="grid gap-3 sm:grid-cols-3">

                    <SummaryCard
                      label="Total rows"
                      value={
                        normalized.total
                      }
                    />

                    <SummaryCard
                      label="Valid rows"
                      value={
                        normalized.valid
                          .length
                      }
                      tone="green"
                    />

                    <SummaryCard
                      label="Rows with issues"
                      value={
                        normalized.invalid
                          .length
                      }
                      tone="rose"
                    />

                  </div>

                  {normalized.invalid.length >
                    0 && (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">

                      <p className="text-sm font-semibold text-amber-900">
                        Rows needing attention
                      </p>

                      <div className="mt-2 grid gap-1 text-xs text-amber-800 sm:grid-cols-2">

                        {normalized.invalid
                          .slice(0, 20)
                          .map((row) => (
                            <p
                              key={
                                row.row_number
                              }
                            >
                              Row{' '}
                              {
                                row.row_number
                              }
                              :{' '}
                              {row.errors?.join(
                                ', '
                              ) ||
                                'Invalid row'}
                            </p>
                          ))}

                      </div>

                    </div>
                  )}

                  <div className="overflow-x-auto rounded-2xl border border-slate-200">

                    <table className="min-w-full text-left text-sm">

                      <thead className="bg-slate-50">

                        <tr>

                          <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">
                            Row
                          </th>

                          <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">
                            Date
                          </th>

                          <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">
                            Description
                          </th>

                          <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">
                            Debit
                          </th>

                          <th className="px-4 py-3 text-xs font-bold uppercase text-slate-500">
                            Credit
                          </th>

                        </tr>

                      </thead>

                      <tbody className="divide-y divide-slate-100">

                        {previewRows.map(
                          (row) => (
                            <tr
                              key={
                                row.row_number
                              }
                            >

                              <td className="px-4 py-3 text-slate-400">
                                {
                                  row.row_number
                                }
                              </td>

                              <td className="px-4 py-3 text-slate-700">
                                {
                                  row.transaction_date
                                }
                              </td>

                              <td className="max-w-[280px] truncate px-4 py-3 text-slate-700">
                                {
                                  row.description
                                }
                              </td>

                              <td className="px-4 py-3 text-slate-700">
                                {row.debit
                                  ? `${organizationCurrency} ${Number(
                                      row.debit
                                    ).toFixed(
                                      2
                                    )}`
                                  : '—'}
                              </td>

                              <td className="px-4 py-3 text-slate-700">
                                {row.credit
                                  ? `${organizationCurrency} ${Number(
                                      row.credit
                                    ).toFixed(
                                      2
                                    )}`
                                  : '—'}
                              </td>

                            </tr>
                          )
                        )}

                      </tbody>

                    </table>

                  </div>

                  <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">

                    <div className="flex items-start gap-3">

                      <SafeIcon
                        icon={FiCpu}
                        className="mt-0.5 shrink-0 text-blue-600"
                      />

                      <div>

                        <p className="text-sm font-semibold text-blue-900">
                          Ready for AI
                          categorization
                        </p>

                        <p className="mt-1 text-xs leading-5 text-blue-700">
                          ProfitYo AI will analyze
                          each valid transaction
                          and suggest an account
                          category from this
                          organization's Chart of
                          Accounts.
                        </p>

                      </div>

                    </div>

                  </div>

                </div>
              )}

            {/* =================================================
               STEP 3 - REVIEW & CATEGORIZE
            ================================================= */}

            {step === 3 && (
              <div className="space-y-5">

                {/* PROCESSING */}

                {busy && (
                  <div className="flex min-h-[420px] flex-col items-center justify-center text-center">

                    <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-blue-50 text-blue-600">

                      <SafeIcon
                        icon={FiCpu}
                        className="animate-pulse text-4xl"
                      />

                    </div>

                    <h3 className="mt-6 text-xl font-semibold text-slate-950">
                      ProfitYO AI is categorizing
                    </h3>

                    <p className="mt-2 max-w-md text-sm text-slate-500">
                      AI is analyzing your
                      transactions and matching
                      them with accounts from your
                      Chart of Accounts.
                    </p>

                    <div className="mt-6 flex items-center gap-2 text-sm text-blue-600">

                      <SafeIcon
                        icon={FiLoader}
                        className="animate-spin"
                      />

                      {categorizationStatus ||
                        'Processing transactions...'}

                    </div>

                    <div className="mt-8 h-2 w-full max-w-md overflow-hidden rounded-full bg-slate-100">

                      <motion.div
                        initial={{
                          width: '10%'
                        }}
                        animate={{
                          width: '85%'
                        }}
                        transition={{
                          duration: 8,
                          ease: 'easeInOut'
                        }}
                        className="h-full rounded-full bg-blue-600"
                      />

                    </div>

                  </div>
                )}

                {/* REVIEW */}

                {!busy &&
                  result && (
                    <>

                      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5">

                        <div className="flex items-start gap-3">

                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-blue-600">

                            <SafeIcon
                              icon={FiCpu}
                              className="text-xl"
                            />

                          </div>

                          <div>

                            <p className="text-sm font-semibold text-blue-900">
                              Review AI
                              categorization
                            </p>

                            <p className="mt-1 text-xs leading-5 text-blue-700">
                              Review the suggested
                              categories below. You
                              can change any category
                              before importing the
                              transactions.
                            </p>

                          </div>

                        </div>

                      </div>

                      <div className="grid gap-3 sm:grid-cols-4">

                        <SummaryCard
                          label="Transactions"
                          value={
                            reviewSummary.total
                          }
                        />

                        <SummaryCard
                          label="Categorized"
                          value={
                            reviewSummary.categorized
                          }
                          tone="green"
                        />

                        <SummaryCard
                          label="Needs Category"
                          value={
                            reviewSummary.missing
                          }
                          tone={
                            reviewSummary.missing >
                            0
                              ? 'amber'
                              : 'green'
                          }
                        />

                        <SummaryCard
                          label="Duplicates"
                          value={
                            reviewSummary.duplicates
                          }
                          tone="slate"
                        />

                      </div>

                      {/* REVIEW TABLE */}

                      <div className="overflow-hidden rounded-2xl border border-slate-200">

                        <div className="overflow-x-auto">

                          <table className="min-w-[1050px] w-full text-left text-sm">

                            <thead className="bg-slate-50">

                              <tr>

                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-slate-500">
                                  Date
                                </th>

                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-slate-500">
                                  Description
                                </th>

                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-slate-500">
                                  Import Account
                                </th>

                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-slate-500">
                                  AI Category
                                </th>

                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-slate-500">
                                  Amount
                                </th>

                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-slate-500">
                                  Confidence
                                </th>

                                <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-slate-500">
                                  AI Reason
                                </th>

                              </tr>

                            </thead>

                            <tbody className="divide-y divide-slate-100">

                              {reviewLoading ? (
                                <tr>

                                  <td
                                    colSpan={7}
                                    className="px-4 py-12 text-center"
                                  >

                                    <div className="flex items-center justify-center gap-2 text-sm text-slate-500">

                                      <SafeIcon
                                        icon={
                                          FiLoader
                                        }
                                        className="animate-spin"
                                      />

                                      Loading review
                                      transactions...

                                    </div>

                                  </td>

                                </tr>
                              ) : reviewRows.length ===
                                0 ? (
                                <tr>

                                  <td
                                    colSpan={7}
                                    className="px-4 py-12 text-center text-sm text-slate-500"
                                  >
                                    No import
                                    transactions
                                    were found.
                                  </td>

                                </tr>
                              ) : (
                                reviewRows.map(
                                  (row) => {
                                    const sourceAccount =
                                      accounts.find(
                                        (
                                          account
                                        ) =>
                                          account.id ===
                                          row.source_account_id
                                      );

                                    const amount =
                                      Number(
                                        row.debit ||
                                          0
                                      ) >
                                      0
                                        ? -Math.abs(
                                            Number(
                                              row.debit ||
                                                0
                                            )
                                          )
                                        : Math.abs(
                                            Number(
                                              row.credit ||
                                                0
                                            )
                                          );

                                    const categoryAccount =
                                      accounts.find(
                                        (
                                          account
                                        ) =>
                                          account.id ===
                                          row.suggested_account_id
                                      );

                                    const confidence =
                                      row.confidence !==
                                        null &&
                                      row.confidence !==
                                        undefined
                                        ? `${Math.round(
                                            Number(
                                              row.confidence
                                            ) *
                                              100
                                          )}%`
                                        : '—';

                                    return (
                                      <tr
                                        key={
                                          row.id
                                        }
                                        className={
                                          row.is_duplicate
                                            ? 'bg-amber-50/50'
                                            : ''
                                        }
                                      >

                                        {/* DATE */}

                                        <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                                          {
                                            row.transaction_date
                                          }
                                        </td>

                                        {/* DESCRIPTION */}

                                        <td className="max-w-[230px] px-4 py-3">

                                          <p className="truncate font-medium text-slate-800">
                                            {
                                              row.description
                                            }
                                          </p>

                                          {row.is_duplicate && (
                                            <span className="mt-1 inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                                              Duplicate
                                            </span>
                                          )}

                                        </td>

                                        {/* IMPORT ACCOUNT */}

                                        <td className="px-4 py-3">

                                          <span className="inline-flex rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                                            {sourceAccount
                                              ? `${
                                                  sourceAccount.code ||
                                                  ''
                                                }${
                                                  sourceAccount.code
                                                    ? ' - '
                                                    : ''
                                                }${
                                                  sourceAccount.name
                                                }`
                                              : selectedAccount
                                                ? `${
                                                    selectedAccount.code ||
                                                    ''
                                                  }${
                                                    selectedAccount.code
                                                      ? ' - '
                                                      : ''
                                                  }${
                                                    selectedAccount.name
                                                  }`
                                                : 'Unknown'}
                                          </span>

                                        </td>

                                        {/* AI CATEGORY */}

                                        <td className="min-w-[260px] px-4 py-3">

                                          <div className="flex items-center gap-2">

                                            <div className="min-w-0 flex-1">

                                              <SearchableSelect
                                                options={accounts}
                                                value={
                                                  row.suggested_account_id ||
                                                  ''
                                                }
                                                onChange={(
                                                  value
                                                ) =>
                                                  updateCategory(
                                                    row,
                                                    value
                                                  )
                                                }
                                                disabled={
                                                  updatingCategoryId ===
                                                    row.id ||
                                                  row.is_duplicate
                                                }
                                                placeholder="Select category..."
                                                searchPlaceholder="Search accounts..."
                                                emptyMessage="No accounts found."
                                                getLabel={(
                                                  account
                                                ) =>
                                                  `${
                                                    account.code ||
                                                    'No code'
                                                  } - ${
                                                    account.name
                                                  }`
                                                }
                                                getSearchText={(
                                                  account
                                                ) => [
                                                  account.code,
                                                  account.name,
                                                  account.type,
                                                  account.sub_type
                                                ]}
                                                menuWidth={
                                                  400
                                                }
                                              />

                                            </div>

                                            {updatingCategoryId ===
                                              row.id && (
                                              <SafeIcon
                                                icon={
                                                  FiLoader
                                                }
                                                className="shrink-0 animate-spin text-slate-400"
                                              />
                                            )}

                                          </div>

                                          {categoryAccount && (
                                            <p className="mt-1 text-[10px] text-slate-400">
                                              AI suggested:{' '}
                                              {
                                                categoryAccount.name
                                              }
                                            </p>
                                          )}

                                        </td>

                                        {/* AMOUNT */}

                                        <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-800">

                                          {amount < 0
                                            ? '-'
                                            : '+'}
                                          {organizationCurrency}{' '}
                                          {Math.abs(
                                            amount
                                          ).toFixed(
                                            2
                                          )}

                                        </td>

                                        {/* CONFIDENCE */}

                                        <td className="whitespace-nowrap px-4 py-3">

                                          {row.is_duplicate ? (
                                            <span className="text-xs text-amber-600">
                                              Duplicate
                                            </span>
                                          ) : (
                                            <span
                                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                                Number(
                                                  row.confidence ||
                                                    0
                                                ) >=
                                                0.8
                                                  ? 'bg-emerald-50 text-emerald-700'
                                                  : Number(
                                                        row.confidence ||
                                                          0
                                                      ) >=
                                                      0.5
                                                    ? 'bg-amber-50 text-amber-700'
                                                    : 'bg-rose-50 text-rose-700'
                                              }`}
                                            >
                                              {
                                                confidence
                                              }
                                            </span>
                                          )}

                                        </td>

                                        {/* REASON */}

                                        <td className="max-w-[260px] px-4 py-3">

                                          <p
                                            className="truncate text-xs text-slate-500"
                                            title={
                                              row.ai_reason ||
                                              ''
                                            }
                                          >
                                            {row.ai_reason ||
                                              'No AI reason provided.'}
                                          </p>

                                        </td>

                                      </tr>
                                    );
                                  }
                                )
                              )}

                            </tbody>

                          </table>

                        </div>

                      </div>

                      {/* MANUAL EDIT NOTICE */}

                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">

                        <div className="flex items-start gap-3">

                          <SafeIcon
                            icon={FiEdit3}
                            className="mt-0.5 shrink-0 text-slate-500"
                          />

                          <div>

                            <p className="text-sm font-semibold text-slate-800">
                              You can edit AI
                              suggestions
                            </p>

                            <p className="mt-1 text-xs leading-5 text-slate-500">
                              Selecting a different
                              account manually changes
                              the transaction to
                              <strong>
                                {' '}
                                Categorized
                              </strong>
                              {' '}
                              and it will be used when
                              the transaction is
                              imported.
                            </p>

                          </div>

                        </div>

                      </div>

                    </>
                  )}

              </div>
            )}

            {/* =================================================
               STEP 4 - FINISH
            ================================================= */}

            {step === 4 &&
              result && (
                <div className="flex min-h-[420px] flex-col items-center justify-center text-center">

                  <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-emerald-50 text-emerald-600">

                    <SafeIcon
                      icon={FiCheckCircle}
                      className="text-4xl"
                    />

                  </div>

                  <h3 className="mt-6 text-2xl font-semibold text-slate-950">
                    CSV import completed
                  </h3>

                  <p className="mt-2 max-w-lg text-sm leading-6 text-slate-600">
                    The transactions have been
                    successfully imported into the
                    General Ledger.
                  </p>

                  <div className="mt-7 grid w-full max-w-2xl gap-3 sm:grid-cols-4">

                    <SummaryCard
                      label="Total"
                      value={
                        result.totalCount
                      }
                    />

                    <SummaryCard
                      label="Staged"
                      value={
                        result.validCount
                      }
                      tone="blue"
                    />

                    <SummaryCard
                      label="AI Categorized"
                      value={
                        result.categorizedCount
                      }
                      tone="green"
                    />

                    <SummaryCard
                      label="Imported"
                      value={
                        result.importedCount ??
                        0
                      }
                      tone="green"
                    />

                  </div>

                  {result.invalidCount >
                    0 && (
                    <p className="mt-5 text-xs text-amber-600">
                      {
                        result.invalidCount
                      }{' '}
                      rows had issues and were
                      not staged.
                    </p>
                  )}

                  {result.failedCount >
                    0 && (
                    <p className="mt-1 text-xs text-rose-600">
                      {
                        result.failedCount
                      }{' '}
                      transactions did not receive
                      an AI category.
                    </p>
                  )}

                  <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 px-6 py-4">

                    <p className="text-sm font-semibold text-emerald-900">
                      {result.importedCount ??
                        0}{' '}
                      transactions imported
                      successfully.
                    </p>

                    <p className="mt-1 text-xs text-emerald-700">
                      The transactions are now
                      available on the Transactions
                      page.
                    </p>

                  </div>

                  <div className="mt-5 rounded-xl bg-slate-50 px-5 py-4 text-xs text-slate-500">
                    The CSV import batch has been
                    completed and the transactions
                    have been posted to the General
                    Ledger.
                  </div>

                </div>
              )}

          </div>

          {/* =================================================
             FOOTER
          ================================================= */}

          <footer className="flex items-center justify-between border-t border-slate-100 px-5 py-4 md:px-7">

            {/* LEFT */}

            <button
              type="button"
              onClick={() => {
                if (
                  step === 0 ||
                  step === 4
                ) {
                  close();
                  return;
                }

                setStep(step - 1);
              }}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            >

              {step > 0 &&
                step < 4 && (
                  <SafeIcon
                    icon={FiChevronLeft}
                  />
                )}

              {step === 4
                ? 'Close'
                : step === 0
                  ? 'Cancel'
                  : 'Back'}

            </button>

            {/* STEP 1 */}

            {step === 1 && (
              <button
                type="button"
                onClick={
                  validateMappingAndPreview
                }
                disabled={
                  busy ||
                  accountsLoading ||
                  !selectedAccountId
                }
                className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
              >
                Validate and preview

                <SafeIcon
                  icon={FiChevronRight}
                />
              </button>
            )}

            {/* STEP 2 */}

            {step === 2 && (
              <button
                type="button"
                onClick={
                  startAiCategorization
                }
                disabled={
                  busy ||
                  !normalized?.valid?.length ||
                  !selectedAccountId
                }
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >

                {busy ? (
                  <>
                    <SafeIcon
                      icon={FiLoader}
                      className="animate-spin"
                    />

                    Processing...
                  </>
                ) : (
                  <>
                    <SafeIcon
                      icon={FiCpu}
                    />

                    Start AI Categorization

                    <SafeIcon
                      icon={
                        FiChevronRight
                      }
                    />
                  </>
                )}

              </button>
            )}

            {/* STEP 3 */}

            {step === 3 &&
              !busy &&
              result && (
                <button
                  type="button"
                  onClick={
                    approveAndImport
                  }
                  disabled={
                    reviewLoading ||
                    !reviewSummary.ready
                  }
                  className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                >

                  <SafeIcon
                    icon={
                      FiCheckCircle
                    }
                  />

                  {reviewSummary.missing >
                  0
                    ? `Assign ${reviewSummary.missing} ${
                        reviewSummary.missing ===
                        1
                          ? 'category'
                          : 'categories'
                      }`
                    : 'Approve & Import'}

                  {reviewSummary.missing ===
                    0 && (
                    <SafeIcon
                      icon={
                        FiChevronRight
                      }
                    />
                  )}

                </button>
              )}

            {/* STEP 4 */}

            {step === 4 &&
              result && (
                <button
                  type="button"
                  onClick={close}
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
                >

                  <SafeIcon
                    icon={FiCheckCircle}
                  />

                  Finish

                </button>
              )}

          </footer>

        </motion.section>
      </div>
    </AnimatePresence>
  );
}